import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { submitContactForm } from 'api/contactUs';
import { PublicLandingFooter, PublicLandingNav } from './LandingShared';

const primaryColor = '#0A7F74';
const deepColor = '#04534B';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  subject?: string;
  message?: string;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[\d\s+\-()]{7,20}$/;

const validate = (form: FormState): FormErrors => {
  const errors: FormErrors = {};
  if (!form.firstName.trim()) errors.firstName = 'First name is required';
  if (!form.lastName.trim()) errors.lastName = 'Last name is required';
  if (!form.email.trim()) errors.email = 'Email is required';
  else if (!emailRegex.test(form.email)) errors.email = 'Invalid email format';
  if (!form.phone.trim()) errors.phone = 'Phone is required';
  else if (!phoneRegex.test(form.phone)) errors.phone = 'Invalid phone format';
  if (!form.subject.trim()) errors.subject = 'Subject is required';
  if (!form.message.trim()) errors.message = 'Message is required';
  return errors;
};

const initialForm: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
};

const ContactPage: React.FC = () => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [apiError, setApiError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setApiError('');
    setSuccess(false);
    setSuccessMessage('');

    try {
      const response = await submitContactForm(form);
      setSuccess(true);
      setSuccessMessage(
        response?.message ||
          'Thank you! Your message has been received. Platform admin will review it soon.'
      );
      setForm(initialForm);
      setErrors({});
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.error ||
        'Something went wrong. Please try again.';
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#fff', px: '1px' }}>
      <PublicLandingNav active="contact" />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 }, px: '1px !important' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: deepColor, mb: 1 }}>
            Contact Us
          </Typography>
          <Typography sx={{ color: '#6b7280' }}>
            Tell us about your group and we will get back to you quickly.
          </Typography>
        </Box>

        <Card elevation={0} sx={{ p: { xs: 2, md: 4 }, border: '1px solid #e5e7eb', borderRadius: 3 }}>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                error={!!errors.subject}
                helperText={errors.subject}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Message"
                name="message"
                value={form.message}
                onChange={handleChange}
                error={!!errors.message}
                helperText={errors.message}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                fullWidth
                disabled={loading}
                onClick={handleSubmit}
                sx={{
                  bgcolor: primaryColor,
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1.25,
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#085f57' }
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Submit'}
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Container>

      <PublicLandingFooter />
    </Box>
  );
};

export default ContactPage;
